import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const StreamStatsContext = createContext();

export const useStreamStats = () => {
  const context = useContext(StreamStatsContext);
  if (!context) {
    throw new Error('useStreamStats must be used within a StreamStatsProvider');
  }
  return context;
};

export const StreamStatsProvider = ({ children }) => {
  // Stops polling once token is detected as invalid to prevent console spam
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const tokenInvalidRef = useRef(false);
  // Load session data from localStorage on mount
  const loadSessionFromStorage = () => {
    try {
      const savedSession = localStorage.getItem('streamSessionStats');
      if (savedSession) {
        const parsed = JSON.parse(savedSession);
        return {
          ...parsed,
          startTime: parsed.startTime ? new Date(parsed.startTime) : null,
          endTime: parsed.endTime ? new Date(parsed.endTime) : null,
          chartData: parsed.chartData || []
        };
      }
    } catch (error) {
      console.error('Error loading session from storage:', error);
    }
    return null;
  };

  const [sessionStats, setSessionStats] = useState(() => {
    const savedSession = loadSessionFromStorage();
    return savedSession || {
      startTime: null,
      endTime: null,
      peakViewers: 0,
      totalViewers: 0,
      peakBandwidth: 0,
      totalBandwidth: 0,
      isActive: false,
      currentViewers: 0,
      currentBandwidth: 0,
      chartData: []
    };
  });

  const [isStreaming, setIsStreaming] = useState(() => {
    const savedSession = loadSessionFromStorage();
    return savedSession ? savedSession.isActive : false;
  });

  // Save session data to localStorage whenever it changes
  useEffect(() => {
    try {
      const sessionToSave = {
        ...sessionStats,
        startTime: sessionStats.startTime ? sessionStats.startTime.toISOString() : null,
        endTime: sessionStats.endTime ? sessionStats.endTime.toISOString() : null
      };
      localStorage.setItem('streamSessionStats', JSON.stringify(sessionToSave));
    } catch (error) {
      console.error('Error saving session to storage:', error);
    }
  }, [sessionStats]);

  // Save streaming state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('streamSessionActive', JSON.stringify(isStreaming));
    } catch (error) {
      console.error('Error saving streaming state to storage:', error);
    }
  }, [isStreaming]);

  // Check if streaming is active
  const checkStreamingStatus = async () => {
    if (tokenInvalidRef.current) return false;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/analytics/real-time', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const activeStreams = response.data.activeChannels || 0;
      const currentlyStreaming = activeStreams > 0;

      // Handle streaming start/stop
      if (currentlyStreaming && !isStreaming) {
        // Streaming just started
        startStreamingSession(response.data);
      } else if (!currentlyStreaming && isStreaming) {
        // Streaming just stopped
        endStreamingSession();
      }

      setIsStreaming(currentlyStreaming);

      // Update current stats if streaming
      if (currentlyStreaming) {
        updateCurrentStats(response.data);
      }

      return currentlyStreaming;
    } catch (error) {
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        // Token expired or invalid — stop polling and signal App to logout
        tokenInvalidRef.current = true;
        setTokenInvalid(true);
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
        return false;
      }
      console.error('Error checking streaming status:', error);
      return false;
    }
  };

  const startStreamingSession = (initialData) => {
    const now = new Date();
    const bandwidthMbps = Math.round((initialData.bandwidthRaw || 0) / 1024 / 1024);
    
    setSessionStats(prev => ({
      ...prev,
      startTime: now,
      endTime: null,
      isActive: true,
      currentViewers: initialData.totalViewers || 0,
      currentBandwidth: initialData.bandwidthRaw || 0,
      peakViewers: Math.max(prev.peakViewers, initialData.totalViewers || 0),
      peakBandwidth: Math.max(prev.peakBandwidth, initialData.bandwidthRaw || 0),
      chartData: [{
        time: `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`,
        viewers: initialData.totalViewers || 0,
        bandwidth: bandwidthMbps // Show actual bandwidth value (1-10 Mbps)
      }]
    }));
  };

  const endStreamingSession = () => {
    const now = new Date();
    setSessionStats(prev => ({
      ...prev,
      endTime: now,
      isActive: false,
      currentViewers: 0,
      currentBandwidth: 0
    }));
  };

  const updateCurrentStats = (data) => {
    const now = new Date();
    const timeLabel = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    setSessionStats(prev => {
      const newViewers = data.totalViewers || 0;
      const newBandwidth = data.bandwidthRaw || 0;
      const bandwidthMbps = Math.round(newBandwidth / 1024 / 1024);
      
      // Update chart data
      const newChartData = [...prev.chartData, {
        time: timeLabel,
        viewers: newViewers,
        bandwidth: bandwidthMbps // Show actual bandwidth value (1-10 Mbps)
      }];
      
      // Keep last 60 points (1 hour with 1-min intervals)
      const updatedChartData = newChartData.slice(-60);
      
      return {
        ...prev,
        currentViewers: newViewers,
        currentBandwidth: newBandwidth,
        peakViewers: Math.max(prev.peakViewers, newViewers),
        peakBandwidth: Math.max(prev.peakBandwidth, newBandwidth),
        totalViewers: prev.totalViewers + newViewers,
        totalBandwidth: prev.totalBandwidth + newBandwidth,
        chartData: updatedChartData
      };
    });
  };

  const resetSessionStats = () => {
    setSessionStats({
      startTime: null,
      endTime: null,
      peakViewers: 0,
      totalViewers: 0,
      peakBandwidth: 0,
      totalBandwidth: 0,
      isActive: false,
      currentViewers: 0,
      currentBandwidth: 0,
      chartData: []
    });
    setIsStreaming(false);
    
    // Clear from localStorage
    try {
      localStorage.removeItem('streamSessionStats');
      localStorage.removeItem('streamSessionActive');
    } catch (error) {
      console.error('Error clearing session from storage:', error);
    }
  };

  // Check status every 1 minute
  useEffect(() => {
    if (tokenInvalid) return; // Token already invalid, don't start new interval
    checkStreamingStatus();
    const interval = setInterval(checkStreamingStatus, 60000); // 1 minute = 60000 ms for more data points

    return () => clearInterval(interval);
  }, [isStreaming, tokenInvalid]);

  const value = {
    sessionStats,
    isStreaming,
    checkStreamingStatus,
    resetSessionStats
  };

  return (
    <StreamStatsContext.Provider value={value}>
      {children}
    </StreamStatsContext.Provider>
  );
};
